import type {
  GetBrandDto,
  StockMachineAttachmentDataDto,
  StockMachineAttachmentDto,
  StockMachineDataDto,
  StockMachineDto
} from '@api/machines'
import { type MachineTypeValues, StockTypes } from '@common/utils'
import {
  MolsonBox,
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader,
  MolsonToggle
} from '@components/reusable'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  type EquipmentType,
  useGetBrandByEquipmentType,
  useGetCatalogAndStockAttachmentsBrandsForMachine
} from '@/shared-queries/machines'
import { StockItemHeader } from '../stock-item-header/StockItemHeader'
import { SelectBrandStepV2 } from './SelectBrandStepV2'
import { SelectMachineStepV2 } from './SelectMachineStepV2'
import { SelectStockType } from './SelectStockTypeV2'

export enum EQUIPMENT_STEPS {
  SELECT_BRAND = 'selectBrand',
  SELECT_EQ = 'selectEquipment',
  SELECT_ITEMS = 'selectItems'
}

type SelectMachineDialogV2Props = {
  open: boolean
  onClose: () => void
  translationPrefix?: string
  machineType?: MachineTypeValues
  onSave: ({
    equipmentId,
    stockType
  }: {
    equipmentId?: number
    stockType?: string
  }) => Promise<null | undefined>
  machineId?: number
  tonnage?: number
  hideStockItems?: boolean
}

export const SelectMachineDialogV2 = ({
  open,
  onClose,
  translationPrefix,
  machineType,
  onSave,
  machineId,
  tonnage,
  hideStockItems
}: SelectMachineDialogV2Props) => {
  const { t } = useTranslation('', {
    keyPrefix: translationPrefix
  })
  const { quoteId: quoteIdParam, specId } = useParams()

  const { data: commonBrands, isLoading: commonBrandsLoading } =
    useGetBrandByEquipmentType(machineType as EquipmentType)

  const { data: withinMachineBrands, isLoading: withinmachineLoading } =
    useGetCatalogAndStockAttachmentsBrandsForMachine(Number(machineId))

  const brands = machineId ? withinMachineBrands : commonBrands
  const isFetchingBrandsLoading = machineId
    ? withinmachineLoading
    : commonBrandsLoading

  const [activeStep, setActiveStep] = useState<EQUIPMENT_STEPS>(
    EQUIPMENT_STEPS.SELECT_BRAND
  )

  const [selectedBrand, setSelectedBrand] = useState<GetBrandDto | null>(null)
  const [selectedMachine, setSelectedMachine] = useState<
    StockMachineAttachmentDto | StockMachineDto | null
  >(null)
  const [selectedStock, setSelectedStock] = useState<
    StockMachineAttachmentDataDto | StockMachineDataDto | null
  >(null)

  const [inStockChecked, setInStockChecked] = useState(false)
  const [nextDisabled, setNextDisabled] = useState<boolean>(false)

  const quoteId = quoteIdParam ?? specId

  const onRowClick = useCallback(
    (machine: StockMachineAttachmentDto | StockMachineDto) =>
      setSelectedMachine(machine),
    []
  )

  const onClickBack = () => {
    switch (activeStep) {
      case EQUIPMENT_STEPS.SELECT_ITEMS: {
        setActiveStep(EQUIPMENT_STEPS.SELECT_EQ)
        setSelectedMachine(null)
        setSelectedStock(null)
        setNextDisabled(false)
        break
      }

      case EQUIPMENT_STEPS.SELECT_EQ: {
        setActiveStep(EQUIPMENT_STEPS.SELECT_BRAND)
        setSelectedBrand(null)
        setSelectedMachine(null)
        setInStockChecked(false)
        setNextDisabled(false)
        break
      }

      default: {
        onClose()
        setSelectedBrand(null)
        setNextDisabled(false)
        break
      }
    }
  }

  const isNextButtonDisabled = () => {
    if (activeStep === EQUIPMENT_STEPS.SELECT_BRAND && selectedBrand?.id) {
      return false
    }

    if (activeStep === EQUIPMENT_STEPS.SELECT_EQ && selectedMachine) {
      return false
    }

    if (activeStep === EQUIPMENT_STEPS.SELECT_ITEMS && selectedStock) {
      return false
    }

    return true
  }

  const handleNextMachineStepSelected = async () => {
    if (!quoteId) {
      return null
    }

    await onSave({
      equipmentId: selectedMachine?.id,
      stockType: selectedMachine?.stockType ?? ''
    })
  }

  const onAddStockItemToQuotation = async () => {
    await onSave({
      equipmentId: selectedStock?.stockId,
      stockType: selectedMachine?.stockType ?? ''
    })
  }

  const onClickNext = async () => {
    setNextDisabled(true)
    try {
      switch (activeStep) {
        case EQUIPMENT_STEPS.SELECT_BRAND:
          setActiveStep(EQUIPMENT_STEPS.SELECT_EQ)
          break

        case EQUIPMENT_STEPS.SELECT_EQ:
          if (selectedMachine?.stockType === StockTypes.FACTORY_ORDER) {
            await handleNextMachineStepSelected()
          } else {
            setActiveStep(EQUIPMENT_STEPS.SELECT_ITEMS)
          }
          break

        case EQUIPMENT_STEPS.SELECT_ITEMS:
          await onAddStockItemToQuotation()
          break
      }
    } finally {
      setNextDisabled(false)
    }
  }

  const onCloseDialog = () => {
    onClose()
    setActiveStep(EQUIPMENT_STEPS.SELECT_BRAND)
    setSelectedBrand(null)
    setSelectedMachine(null)
    setSelectedStock(null)
    setInStockChecked(false)
    setNextDisabled(false)
  }

  return (
    <MolsonDialog
      fullWidth={true}
      maxWidth="lg"
      onClose={onCloseDialog}
      open={open}
    >
      <MolsonDialogHeader
        title={
          activeStep === EQUIPMENT_STEPS.SELECT_BRAND
            ? t('brands')
            : (selectedBrand?.name as string)
        }
        handleCloseDialog={onCloseDialog}
      />
      <MolsonBox pb={2}>
        {activeStep === EQUIPMENT_STEPS.SELECT_EQ && !hideStockItems && (
          <MolsonBox
            alignItems="center"
            display="flex"
            justifyContent="end"
            mt={2}
          >
            <MolsonToggle
              sx={{
                '.MuiTypography-root': {
                  fontWeight: 700
                }
              }}
              checked={inStockChecked}
              label={t('inStockItemsLabel') ?? ''}
              onChange={() => setInStockChecked(checked => !checked)}
            />
          </MolsonBox>
        )}

        {activeStep === EQUIPMENT_STEPS.SELECT_ITEMS && (
          <StockItemHeader
            brand={selectedBrand?.name}
            equipment={selectedMachine}
          />
        )}
      </MolsonBox>

      <MolsonDialogContent
        sx={{
          minHeight: 160,
          padding:
            activeStep === EQUIPMENT_STEPS.SELECT_BRAND
              ? '0px 12px 24px 12px'
              : '0px 0px 24px 0px'
        }}
      >
        {activeStep === EQUIPMENT_STEPS.SELECT_BRAND &&
          !isFetchingBrandsLoading && (
            <SelectBrandStepV2
              setSelectedBrand={setSelectedBrand}
              selectedBrand={selectedBrand}
              translationPrefix="common"
              brands={brands}
            />
          )}

        {activeStep === EQUIPMENT_STEPS.SELECT_EQ && (
          <SelectMachineStepV2
            translationPrefix="common"
            inStockChecked={inStockChecked}
            brandId={selectedBrand?.id}
            machineType={machineType}
            onRowClick={onRowClick}
            tonnage={tonnage}
            hideStockItems={hideStockItems}
          />
        )}

        {activeStep === EQUIPMENT_STEPS.SELECT_ITEMS && (
          <SelectStockType
            equipment={selectedMachine}
            onRowClick={setSelectedStock}
            machineType={machineType}
          />
        )}
      </MolsonDialogContent>

      <MolsonDialogActions>
        <MolsonButton
          color="secondary"
          onClick={onClickBack}
          variant="outlined"
        >
          {activeStep === EQUIPMENT_STEPS.SELECT_ITEMS ||
          activeStep === EQUIPMENT_STEPS.SELECT_EQ
            ? t('back')
            : t('cancel')}
        </MolsonButton>
        <MolsonButton
          disabled={isNextButtonDisabled() || nextDisabled}
          onClick={onClickNext}
          variant="contained"
        >
          {t('next')}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
